import { Calendar, GitBranch, Code, Trash2, Edit, Activity } from 'lucide-react';
import { Card, Badge, Button } from '../../../shared/ui';

interface Project {
    id: string;
    name: string;
    description: string;
    language: string;
    lastAnalysis: string;
    healthScore: number;
    issuesCount: { critical: number; high: number; medium: number; low: number };
    branch: string;
    status: 'active' | 'archived';
}

interface ProjectCardProps {
    project: Project;
    viewMode: 'grid' | 'list';
    onClick: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

export function ProjectCard({ project, viewMode, onClick, onEdit, onDelete }: ProjectCardProps) {
    const totalIssues = Object.values(project.issuesCount).reduce((sum, count) => sum + count, 0);
    const criticalCount = project.issuesCount.critical + project.issuesCount.high;

    // Health score color
    const getHealthColor = (score: number) => {
        if (score >= 90) return 'text-success';
        if (score >= 70) return 'text-warning';
        return 'text-error';
    };

    if (viewMode === 'list') {
        return (
            <Card
                variant="hover"
                padding="md"
                corners="asymmetric"
                className="cursor-pointer group"
                onClick={onClick}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6 flex-1">
                        {/* Project Info */}
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-text-primary font-mono group-hover:text-primary transition-colors">
                                {project.name}
                            </h3>
                            <p className="text-sm text-text-muted mt-1">{project.description}</p>
                        </div>

                        {/* Language */}
                        <div className="flex items-center gap-2">
                            <Code className="h-4 w-4 text-text-secondary" />
                            <span className="text-sm text-text-secondary font-mono">{project.language}</span>
                        </div>

                        {/* Health Score */}
                        <div className="text-center min-w-[80px]">
                            <div className={`text-2xl font-bold font-mono ${getHealthColor(project.healthScore)}`}>
                                {project.healthScore}
                            </div>
                            <div className="text-xs text-text-muted font-mono">Health</div>
                        </div>

                        {/* Issues */}
                        <div className="flex items-center gap-2">
                            {criticalCount > 0 && (
                                <Badge severity="critical">{criticalCount}</Badge>
                            )}
                            <span className="text-sm text-text-secondary font-mono">
                                {totalIssues} issues
                            </span>
                        </div>

                        {/* Last Analysis */}
                        <div className="text-sm text-text-muted font-mono min-w-[120px]">
                            {getRelativeTime(project.lastAnalysis)}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit();
                            }}
                            icon={<Edit className="h-4 w-4" />}
                        />
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            icon={<Trash2 className="h-4 w-4 text-error" />}
                        />
                    </div>
                </div>
            </Card>
        );
    }

    // Grid View
    return (
        <Card
            variant="hover"
            padding="lg"
            corners="asymmetric"
            className="cursor-pointer group relative"
            onClick={onClick}
        >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                    <h3 className="text-xl font-semibold text-text-primary font-mono group-hover:text-primary transition-colors">
                        {project.name}
                    </h3>
                    <p className="text-sm text-text-muted mt-1 line-clamp-2">{project.description}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit();
                        }}
                        icon={<Edit className="h-4 w-4" />}
                    />
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        icon={<Trash2 className="h-4 w-4 text-error" />}
                    />
                </div>
            </div>

            {/* Metrics */}
            <div className="space-y-3">
                {/* Health Score */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-text-secondary" />
                        <span className="text-sm text-text-secondary font-mono">Health Score</span>
                    </div>
                    <div className={`text-2xl font-bold font-mono ${getHealthColor(project.healthScore)}`}>
                        {project.healthScore}
                    </div>
                </div>

                {/* Issues Breakdown */}
                <div className="flex items-center gap-2 flex-wrap">
                    {project.issuesCount.critical > 0 && (
                        <Badge severity="critical">{project.issuesCount.critical} Critical</Badge>
                    )}
                    {project.issuesCount.high > 0 && (
                        <Badge severity="high">{project.issuesCount.high} High</Badge>
                    )}
                    {project.issuesCount.medium > 0 && (
                        <Badge severity="medium">{project.issuesCount.medium} Mid</Badge>
                    )}
                    {project.issuesCount.low > 0 && (
                        <Badge severity="low">{project.issuesCount.low} Low</Badge>
                    )}
                </div>

                {/* Metadata */}
                <div className="pt-3 border-t border-border space-y-2">
                    <div className="flex items-center gap-2 text-sm text-text-muted font-mono">
                        <Code className="h-4 w-4" />
                        {project.language}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted font-mono">
                        <GitBranch className="h-4 w-4" />
                        {project.branch}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted font-mono">
                        <Calendar className="h-4 w-4" />
                        {getRelativeTime(project.lastAnalysis)}
                    </div>
                </div>
            </div>
        </Card>
    );
}

// Helper function to format relative time
function getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}
